import React, { useState, useEffect, useCallback } from 'react';
import { getTerrain, getTraceTerrain, geocoderTerrain, getTechniciens, updateTechnicien } from '../services/api';

/**
 * GPS — la journée des techniciens, relevée au téléphone
 *
 * Aucun pointage n'est demandé au technicien : son application dépose sa
 * position toutes les deux minutes quand elle est ouverte, et le serveur la
 * rapproche des rendez-vous du jour. À cinq cents mètres d'une adresse, il y
 * est ; à huit cents, il en est parti.
 *
 * La page dit toujours d'où elle tient ce qu'elle affiche, parce que le
 * dispositif a des trous connus : une application fermée ne parle pas, une
 * adresse que la Base Adresse Nationale ne trouve pas n'est pas détectable, et
 * un kilométrage calculé sur des adresses n'est pas un itinéraire. Un chiffre
 * dont on ignore la provenance ne sert à personne.
 */

const RAFRAICHISSEMENT_MS = 60 * 1000;

const aujourdhui = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Paris' });

const heure = iso => iso
  ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
  : '—';

/** Une durée dite comme on la dit à l'oral : « 3 min », « 1 h 40 ». */
function duree(ms) {
  if (ms === null || ms === undefined) return '—';
  const min = Math.round(ms / 60000);
  if (min < 1) return 'moins d’une minute';
  if (min < 60) return min + ' min';
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r ? h + ' h ' + String(r).padStart(2, '0') : h + ' h';
}

/** L'âge d'une position, et la couleur que cet âge mérite. */
function fraicheur(ms) {
  if (ms === null || ms === undefined) return { texte: '—', couleur: 'var(--text3)' };
  const min = Math.round(ms / 60000);
  if (min <= 5)  return { texte: 'à l’instant',          couleur: 'var(--green)' };
  if (min <= 20) return { texte: 'il y a ' + min + ' min', couleur: 'var(--green)' };
  if (min <= 60) return { texte: 'il y a ' + min + ' min', couleur: 'var(--amber)' };
  return { texte: 'il y a ' + duree(ms), couleur: 'var(--text3)' };
}

/* ─── L'état d'un technicien, en une phrase ──────────────────────────────── */

/**
 * Cinq états, et un seul s'affiche.
 *
 * L'ordre compte : un refus de géolocalisation passe devant tout le reste,
 * parce qu'il explique l'absence de tout le reste.
 */
function etatDe(t, estAujourdhui) {
  if (t.refus) return {
    cle: 'refus', couleur: 'var(--accent)',
    titre: 'Géolocalisation refusée',
    detail: 'Le technicien a refusé l’autorisation sur son téléphone, ou l’a coupée dans ses réglages.',
  };
  if (!t.derniere) return {
    cle: 'muet', couleur: 'var(--text3)',
    titre: estAujourdhui ? 'Aucune position aujourd’hui' : 'Aucune position ce jour-là',
    detail: 'Son application n’a pas été ouverte, ou pas depuis un endroit couvert.',
  };
  if (t.ici) return {
    cle: 'surplace', couleur: 'var(--green)',
    titre: 'Sur place — ' + t.ici.ref_commande,
    detail: 'Arrivé à ' + heure(t.ici.arrivee) + ', soit depuis ' + duree(t.ici.depuis_ms) + '.',
  };
  if (t.chez_lui) return {
    cle: 'maison', couleur: 'var(--amber)',
    titre: 'Chez lui — ' + (t.derniere.commune || t.domicile.ville),
    detail: 'Sa dernière position est dans sa commune de résidence. Le domicile n’est '
      + 'connu qu’à la ville : c’est sa commune, pas son pas de porte.',
  };
  const age = t.derniere.age_ms;
  if (estAujourdhui && age > 2 * 3600 * 1000) return {
    cle: 'perdu', couleur: 'var(--text3)',
    titre: 'Sans nouvelles depuis ' + duree(age),
    detail: 'Dernière position connue : ' + (t.derniere.commune || 'inconnue') + '.',
  };
  return {
    cle: 'route', couleur: 'var(--blue)',
    titre: t.derniere.commune ? 'En déplacement — ' + t.derniere.commune : 'En déplacement',
    detail: t.derniere.libelle || '',
  };
}

/* ─── Qui s'affiche ici ──────────────────────────────────────────────────── */

/**
 * Le panneau des réglages.
 *
 * La page montre tous les comptes de l'application, et la plupart ne
 * travaillent plus : leurs cartes vides noient les quatre techniciens qu'on
 * cherche. Le drapeau posé ici est celui de la page Techniciens, pas un second
 * réglage qui lui mentirait — un compte marqué « ne travaille plus » disparaît
 * d'ici ET perd l'accès à l'application. C'est la même chose dans la vraie vie.
 *
 * Un technicien créé dans la page Techniciens apparaît ici tout seul, et sur la
 * page, sans rien avoir à cocher : il faut déclarer les départs, pas les
 * arrivées.
 */
function Reglages({ surChangement }) {
  const [liste, setListe] = useState(null);
  const [occupe, setOccupe] = useState('');
  const [erreur, setErreur] = useState('');

  const lire = useCallback(() => {
    getTechniciens()
      .then(r => setListe((r.techniciens || []).slice().sort((a, b) =>
        String(a.zoho_nom || '').localeCompare(String(b.zoho_nom || ''), 'fr', { numeric: true }))))
      .catch(e => setErreur(e.response?.data?.error || e.message));
  }, []);
  useEffect(() => { lire(); }, [lire]);

  const basculer = async (t) => {
    setOccupe(t.id); setErreur('');
    try {
      await updateTechnicien(t.id, {
        nom: t.nom, prenom: t.prenom, zoho_nom: t.zoho_nom, secteur: t.secteur || '',
        disabled: !t.disabled,
      });
      lire();
      surChangement();
    } catch (e) {
      setErreur(e.response?.data?.error || e.message);
    } finally { setOccupe(''); }
  };

  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderLeft: '3px solid var(--blue)', borderRadius: 8,
      padding: 14, marginBottom: 16,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
        Qui s’affiche sur cette page
      </div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10, lineHeight: 1.55 }}>
        Décocher un technicien le marque « ne travaille plus » : il disparaît d’ici et son
        compte ne peut plus se connecter à l’application. C’est le même réglage que le
        bouton de la page Techniciens. Un technicien créé là-bas apparaît ici sans rien faire.
      </div>
      {erreur && <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 8 }}>{erreur}</div>}
      {!liste && <div style={{ fontSize: 12, color: 'var(--text3)' }}>Lecture…</div>}
      {liste && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 6 }}>
          {liste.map(t => (
            <label key={t.id} title={t.disabled ? 'Ne travaille plus' : 'En activité'} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
              borderRadius: 6, cursor: occupe === t.id ? 'wait' : 'pointer',
              background: t.disabled ? 'transparent' : 'var(--bg)',
              border: '1px solid ' + (t.disabled ? 'var(--border)' : 'var(--border2)'),
              opacity: occupe === t.id ? .5 : 1,
            }}>
              <input type="checkbox" checked={!t.disabled} disabled={!!occupe}
                onChange={() => basculer(t)} />
              <span style={{ minWidth: 0 }}>
                <span style={{
                  fontSize: 12, fontWeight: 700,
                  color: t.disabled ? 'var(--text3)' : 'var(--text)',
                  textDecoration: t.disabled ? 'line-through' : undefined,
                }}>{t.zoho_nom}</span>
                <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 6 }}>
                  {[t.prenom, t.nom].filter(Boolean).join(' ')}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Les étiquettes ─────────────────────────────────────────────────────── */

function Etiquette({ couleur, children, titre }) {
  return (
    <span title={titre} style={{
      display: 'inline-block', padding: '1px 6px', borderRadius: 4,
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .3,
      color: couleur, border: '1px solid ' + couleur, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

/* ─── La fiche d'un technicien ───────────────────────────────────────────── */

function Fiche({ t, date, estAujourdhui }) {
  const [ouvert, setOuvert] = useState(false);
  const etat = etatDe(t, estAujourdhui);
  const f = t.derniere ? fraicheur(t.derniere.age_ms) : null;

  const nonVues = t.interventions.filter(i => !i.vue).length;
  const nonLocalisables = t.interventions.filter(i => i.approche).length;

  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderLeft: '3px solid ' + etat.couleur, borderRadius: 8, padding: 14,
    }}>
      {/* Qui, et dans quel état */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
            {t.zoho_nom}
            <span style={{ fontWeight: 400, color: 'var(--text2)' }}>  {t.nom}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
            {t.secteur || 'secteur non renseigné'}
            {t.telephone ? ' · ' + t.telephone : ''}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: etat.couleur }}>{etat.titre}</div>
          {f && (
            <div style={{ fontSize: 11, color: f.couleur, marginTop: 2 }}>
              relevé {f.texte} · {heure(t.derniere.at)}
            </div>
          )}
        </div>
      </div>

      {etat.detail && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.5 }}>
          {etat.detail}
        </div>
      )}

      {/* Où exactement, et sur une carte si on veut vérifier */}
      {t.derniere && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
          <a href={'https://www.google.com/maps?q=' + t.derniere.lat + ',' + t.derniere.lon}
            target="_blank" rel="noreferrer"
            style={{ fontSize: 11, color: 'var(--blue)', textDecoration: 'none' }}>
            voir sur la carte ↗
          </a>
          {t.derniere.imprecise && (
            <Etiquette couleur="var(--amber)"
              titre={'Le téléphone donne cette position à ' + t.derniere.imprecise + ' m près : trop imprécise pour reconnaître une arrivée.'}>
              ± {t.derniere.imprecise} m
            </Etiquette>
          )}
          {t.derniere.source === 'ouverture' && (
            <Etiquette couleur="var(--text3)" titre="Première position de la session, à l’ouverture de l’application.">
              à l’ouverture
            </Etiquette>
          )}
        </div>
      )}

      {/* Kilométrage */}
      <div style={{
        display: 'flex', gap: 18, marginTop: 12, paddingTop: 10,
        borderTop: '1px solid var(--border)', flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
            {t.km.km_prevus} km
          </div>
          <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .3 }}
            title={t.km.depuis_domicile
              ? 'Domicile → les ' + t.km.etapes_geocodees + ' rendez-vous dans l’ordre prévu → domicile. Vol d’oiseau × 1,3, ce n’est pas un itinéraire.'
              : 'Domicile non renseigné : seuls les trajets entre rendez-vous sont comptés. Renseignez-le dans la page Techniciens.'}>
            prévus {t.km.depuis_domicile ? '' : '(sans domicile)'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text2)', fontVariantNumeric: 'tabular-nums' }}>
            {t.km.km_releves} km
          </div>
          <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .3 }}
            title={'Somme des ' + t.km.points_releves + ' points relevés, donc seulement pendant que l’application était ouverte. Un trajet fait application fermée n’y est pas.'}>
            relevés
          </div>
        </div>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
            {t.interventions.length - nonVues}/{t.interventions.length}
          </div>
          <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .3 }}
            title="Rendez-vous où une présence de plus de cinq minutes a été relevée.">
            rendez-vous vus
          </div>
        </div>
        {nonLocalisables > 0 && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--amber)', fontVariantNumeric: 'tabular-nums' }}>
              {nonLocalisables}
            </div>
            <div style={{ fontSize: 10, color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: .3 }}
              title="Adresses que la Base Adresse Nationale n’a pas trouvées à la rue : aucune arrivée ne peut y être détectée.">
              non localisables
            </div>
          </div>
        )}
      </div>

      <button onClick={() => setOuvert(o => !o)} style={{
        marginTop: 10, padding: '4px 9px', fontSize: 11, fontWeight: 600,
        background: 'transparent', color: 'var(--blue)',
        border: '1px solid var(--border2)', borderRadius: 5, cursor: 'pointer',
      }}>
        {ouvert ? 'masquer la journée' : 'voir la journée'}
      </button>

      {ouvert && <Journee t={t} date={date} />}
    </div>
  );
}

/* ─── Le détail d'une journée ────────────────────────────────────────────── */

function Journee({ t, date }) {
  const cellule = { padding: '5px 8px', fontSize: 12, color: 'var(--text)', verticalAlign: 'top' };
  const entete = {
    padding: '5px 8px', fontSize: 10, fontWeight: 700, color: 'var(--text3)',
    textTransform: 'uppercase', letterSpacing: .3, textAlign: 'left',
    borderBottom: '1px solid var(--border)',
  };

  return (
    <div style={{ marginTop: 12, display: 'grid', gap: 14 }}>
      {/* Les rendez-vous du jour */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>
          Ses rendez-vous
        </div>
        {t.interventions.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Aucun rendez-vous à son planning ce jour-là.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={entete}>Heure</th>
                <th style={entete}>Commande</th>
                <th style={entete}>Client</th>
                <th style={entete}>Adresse</th>
                <th style={entete}>Relevé</th>
              </tr></thead>
              <tbody>
                {t.interventions
                  .slice()
                  .sort((a, b) => String(a.heure || '').localeCompare(String(b.heure || '')))
                  .map(i => (
                    <tr key={i.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ ...cellule, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {i.heure || '—'}
                      </td>
                      <td style={cellule}>
                        <a href={'/dossier/' + i.id} target="_blank" rel="noreferrer"
                          style={{ color: 'var(--blue)', textDecoration: 'none', fontFamily: 'var(--mono)', fontSize: 11 }}>
                          {i.ref_commande}
                        </a>
                      </td>
                      <td style={{ ...cellule, color: 'var(--text2)' }}>{i.client || '—'}</td>
                      <td style={{ ...cellule, color: 'var(--text2)', fontSize: 11 }}>{i.adresse}</td>
                      <td style={cellule}>
                        {i.approche
                          ? <Etiquette couleur="var(--amber)"
                              titre={i.approche === 'voie_introuvable'
                                ? 'La rue n’a pas été trouvée dans la Base Adresse Nationale : le point retenu est le centre de la commune, trop grossier pour un rayon de 500 m.'
                                : 'Adresse incomplète dans la commande : seul le centre de la commune est connu.'}>
                              non localisable
                            </Etiquette>
                          : i.vue
                            ? <span style={{ color: 'var(--green)', fontWeight: 600 }}>présence relevée</span>
                            : <span style={{ color: 'var(--text3)' }}>rien</span>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Les passages détectés */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>
          Passages relevés
        </div>
        {t.passages.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>
            Aucun passage détecté. Soit l’application est restée fermée, soit aucun de ses
            rendez-vous n’a d’adresse localisable à la rue.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={entete}>Commande</th>
                <th style={entete}>Arrivée</th>
                <th style={entete}>Départ</th>
                <th style={entete}>Durée</th>
                <th style={entete}>Au plus près</th>
              </tr></thead>
              <tbody>
                {t.passages.map((p, n) => (
                  <tr key={p.dossier_id + '-' + n} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={cellule}>
                      <a href={'/dossier/' + p.dossier_id} target="_blank" rel="noreferrer"
                        style={{ color: 'var(--blue)', textDecoration: 'none', fontFamily: 'var(--mono)', fontSize: 11 }}>
                        {p.ref_commande}
                      </a>
                      <div style={{ fontSize: 10, color: 'var(--text3)' }}>{p.ville}</div>
                    </td>
                    <td style={{ ...cellule, fontVariantNumeric: 'tabular-nums' }}>{heure(p.arrivee)}</td>
                    <td style={{ ...cellule, fontVariantNumeric: 'tabular-nums' }}>
                      {p.depart ? heure(p.depart) : <span style={{ color: 'var(--green)' }}>sur place</span>}
                      {p.depart_estime && (
                        <div style={{ marginTop: 2 }}>
                          <Etiquette couleur="var(--amber)"
                            titre="L’application s’est fermée : le départ est posé au dernier relevé reçu. La durée est un minimum.">
                            estimé
                          </Etiquette>
                        </div>
                      )}
                    </td>
                    <td style={{ ...cellule, fontVariantNumeric: 'tabular-nums' }}>
                      {duree(p.duree_ms)}
                      {p.bref && (
                        <div style={{ marginTop: 2 }}>
                          <Etiquette couleur="var(--text3)"
                            titre="Moins de cinq minutes dans le rayon : il est passé devant, ce n’est pas compté comme une visite.">
                            passage
                          </Etiquette>
                        </div>
                      )}
                    </td>
                    <td style={{ ...cellule, color: 'var(--text2)', fontVariantNumeric: 'tabular-nums' }}>
                      {p.distance_min_m} m
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Trace zoho_nom={t.zoho_nom} date={date} />
    </div>
  );
}

/** Le tracé brut, pour le jour où un chiffre est contesté. */
function Trace({ zoho_nom, date }) {
  const [pts, setPts] = useState(null);
  const [charge, setCharge] = useState(false);

  const charger = () => {
    setCharge(true);
    getTraceTerrain(zoho_nom, date)
      .then(d => setPts(d.points || []))
      .catch(() => setPts([]));
  };

  if (!charge) return (
    <button onClick={charger} style={{
      alignSelf: 'start', padding: '4px 9px', fontSize: 11, fontWeight: 600,
      background: 'transparent', color: 'var(--text2)',
      border: '1px solid var(--border2)', borderRadius: 5, cursor: 'pointer',
    }}>voir les points relevés</button>
  );

  if (!pts) return <div style={{ fontSize: 12, color: 'var(--text3)' }}>Chargement…</div>;
  if (!pts.length) return <div style={{ fontSize: 12, color: 'var(--text3)' }}>Aucun point ce jour-là.</div>;

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>
        {pts.length} points relevés
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, lineHeight: 1.5 }}>
        Un point n’est gardé que s’il s’est éloigné de cent cinquante mètres du précédent, ou si
        cinq minutes ont passé : la trace dessine le trajet sans le peser.
      </div>
      <div style={{
        maxHeight: 180, overflowY: 'auto', background: 'var(--bg)',
        border: '1px solid var(--border)', borderRadius: 6, padding: 8,
      }}>
        {pts.map((p, n) => (
          <div key={n} style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'var(--mono)' }}>
            {heure(p.at)}{'  '}
            <a href={'https://www.google.com/maps?q=' + p.lat + ',' + p.lon}
              target="_blank" rel="noreferrer" style={{ color: 'var(--blue)', textDecoration: 'none' }}>
              {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
            </a>
            {p.m !== null && p.m !== undefined ? '  ± ' + p.m + ' m' : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── La page ────────────────────────────────────────────────────────────── */

export default function Terrain() {
  const [date, setDate] = useState(aujourdhui());
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [vu, setVu] = useState(null);
  const [geocodage, setGeocodage] = useState(null);
  const [auto, setAuto] = useState(true);
  const [reglages, setReglages] = useState(false);

  const charger = useCallback(() => getTerrain(date)
    .then(d => { setData(d); setVu(new Date()); setErreur(null); })
    .catch(() => setErreur('Lecture impossible'))
    .finally(() => setChargement(false)), [date]);

  useEffect(() => { setChargement(true); charger(); }, [charger]);

  // Une page de surveillance qu'il faut recharger à la main ne surveille rien.
  useEffect(() => {
    if (!auto) return;
    const t = setInterval(charger, RAFRAICHISSEMENT_MS);
    return () => clearInterval(t);
  }, [auto, charger]);

  const geocoder = async () => {
    setGeocodage('en cours');
    try {
      const r = await geocoderTerrain(date);
      setGeocodage(r.precises + ' adresses à la rue, ' + r.approchees + ' au centre de la commune'
        + (r.introuvables ? ', ' + r.introuvables + ' introuvables' : ''));
      charger();
    } catch { setGeocodage('échec'); }
  };

  const estAujourdhui = date === aujourdhui();

  if (chargement) return (
    <>
      <div className="topbar"><div className="topbar-title">GPS</div></div>
      <div className="page"><div style={{ color: 'var(--text3)', padding: 40, textAlign: 'center' }}>Chargement…</div></div>
    </>
  );

  const techs = (data && data.techniciens) || [];
  const compte = cle => techs.filter(t => etatDe(t, estAujourdhui).cle === cle).length;
  const sansDomicile = techs.filter(t => !t.domicile_renseigne).length;

  const resume = [
    { label: 'sur place',          n: compte('surplace'), couleur: 'var(--green)' },
    { label: 'en déplacement',     n: compte('route'),    couleur: 'var(--blue)' },
    { label: 'chez eux',           n: compte('maison'),   couleur: 'var(--amber)' },
    { label: 'sans nouvelles',     n: compte('perdu'),    couleur: 'var(--text2)' },
    { label: 'aucune position',    n: compte('muet'),     couleur: 'var(--text3)' },
    { label: 'refus',              n: compte('refus'),    couleur: 'var(--accent)' },
  ];

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">GPS</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="date" value={date} max={aujourdhui()} onChange={e => setDate(e.target.value)}
            style={{
              padding: '6px 9px', fontSize: 13, background: 'var(--bg)',
              border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)',
            }} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text3)', cursor: 'pointer' }}>
            <input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} />
            rafraîchir seul
          </label>
          <button onClick={() => setReglages(r => !r)} style={{
            padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: reglages ? 'var(--blue)' : 'transparent',
            color: reglages ? '#fff' : 'var(--blue)',
            border: '1px solid ' + (reglages ? 'var(--blue)' : 'var(--border2)'),
            borderRadius: 6,
          }}>réglages</button>
          {vu && (
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              lu à {vu.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>
      </div>

      <div className="page">
        {erreur && (
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--accent)', borderRadius: 6,
            padding: 10, marginBottom: 12, fontSize: 13, color: 'var(--accent)',
          }}>{erreur}</div>
        )}

        {reglages && <Reglages surChangement={charger} />}

        {/* Le résumé */}
        <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 16 }}>
          {resume.map(r => (
            <div key={r.label}>
              <div style={{ fontSize: 24, fontWeight: 700, color: r.couleur, fontVariantNumeric: 'tabular-nums' }}>
                {r.n}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .3 }}>
                {r.label}
              </div>
            </div>
          ))}
        </div>

        {/* Ce qui empêche la page d'être complète, dit avant le reste */}
        {data && data.qualite && (data.qualite.sans_point > 0 || data.qualite.adresse_approchee > 0) && (
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderLeft: '3px solid var(--amber)', borderRadius: 6,
            padding: 12, marginBottom: 14, fontSize: 12, color: 'var(--text2)', lineHeight: 1.6,
          }}>
            Sur les {data.qualite.interventions} interventions de la journée,{' '}
            <strong style={{ color: 'var(--amber)' }}>
              {data.qualite.adresse_approchee + data.qualite.sans_point}
            </strong>{' '}
            n’ont pas d’adresse localisable à la rue — la Base Adresse Nationale ne trouve pas la
            voie, et le point retenu est le centre de la commune. Aucune arrivée n’y est détectée :
            à Marseille le centre de la commune est à six kilomètres de certaines rues, et une
            arrivée annoncée là serait fausse.
            <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={geocoder} disabled={geocodage === 'en cours'} style={{
                padding: '5px 10px', fontSize: 12, fontWeight: 600,
                background: 'transparent', color: 'var(--blue)',
                border: '1px solid var(--border2)', borderRadius: 5,
                cursor: geocodage === 'en cours' ? 'wait' : 'pointer',
              }}>
                {geocodage === 'en cours' ? 'géocodage…' : 'reprendre le géocodage'}
              </button>
              {geocodage && geocodage !== 'en cours' && (
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{geocodage}</span>
              )}
            </div>
          </div>
        )}

        {sansDomicile > 0 && (
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderLeft: '3px solid var(--amber)', borderRadius: 6,
            padding: 12, marginBottom: 14, fontSize: 12, color: 'var(--text2)', lineHeight: 1.6,
          }}>
            {sansDomicile} technicien{sansDomicile > 1 ? 's' : ''} sans domicile renseigné : leur
            kilométrage ne compte que les trajets entre rendez-vous, pas le départ du matin ni le
            retour du soir — ce qui est souvent la moitié de la journée. Le champ se remplit dans
            la page <a href="/techniciens" style={{ color: 'var(--blue)' }}>Techniciens</a>.
          </div>
        )}

        {/* Les fiches */}
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))' }}>
          {techs.map(t => (
            <Fiche key={t.zoho_nom} t={t} date={date} estAujourdhui={estAujourdhui} />
          ))}
        </div>

        {/* D'où viennent ces chiffres */}
        <div style={{
          marginTop: 22, background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 8, padding: 14, fontSize: 12, color: 'var(--text2)', lineHeight: 1.7,
          maxWidth: 900,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 8 }}>
            À savoir sur ces chiffres
          </div>
          <div>
            <strong>Rien n’est pointé par le technicien.</strong> Son application dépose sa position
            toutes les deux minutes, et seulement quand elle est au premier plan. À moins de{' '}
            {(data && data.reglages ? data.reglages.rayon_arrivee_m : 500)} m d’une adresse, il y
            est ; au delà de {(data && data.reglages ? data.reglages.rayon_depart_m : 800)} m, il en
            est parti. L’écart entre les deux évite qu’un relevé qui flotte ouvre et ferme dix fois
            le même passage.
          </div>
          <div style={{ marginTop: 6 }}>
            <strong>Une application fermée ne dit rien.</strong> Le départ d’un passage resté ouvert
            est alors posé au dernier relevé reçu et marqué « estimé » : la durée affichée est un
            minimum, pas une mesure. Un technicien qui ferme l’application en roulant laisse un trou
            dans ses kilomètres relevés.
          </div>
          <div style={{ marginTop: 6 }}>
            <strong>Les kilomètres prévus ne sont pas un itinéraire.</strong> C’est le vol d’oiseau
            domicile → rendez-vous dans l’ordre prévu → domicile, majoré de{' '}
            {Math.round(((data && data.reglages ? data.reglages.coef_route : 1.3) - 1) * 100)} % pour
            le détour routier. À comparer aux kilomètres relevés, jamais à rembourser tel quel.
          </div>
          <div style={{ marginTop: 6 }}>
            <strong>Moins de cinq minutes sur place n’est pas une visite.</strong> Le passage est
            gardé et marqué comme tel : il est passé devant, ou il s’est garé le temps d’un appel.
          </div>
        </div>
      </div>
    </>
  );
}
